import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import MentionInput from "./MentionInput";
import api from "../services/api";
import { getProfilePicture } from "../utils/avatar";
import {
  FaRegComment,
  FaHeart,
  FaRetweet,
  FaUserPlus,
  FaAt,
  FaTimes,
} from "react-icons/fa";

const TOAST_DURATION = 8000;

const typeConfig = {
  comment: {
    icon: FaRegComment,
    color: "spiders-success",
    verb: "commented on your post",
  },
  reply: {
    icon: FaRegComment,
    color: "spiders-success",
    verb: "replied to your comment",
  },
  thread_activity: {
    icon: FaRegComment,
    color: "spiders-success",
    verb: "commented in a thread you participated in",
  },
  like: {
    icon: FaHeart,
    color: "spiders-error",
    verb: "liked your post",
  },
  repost: {
    icon: FaRetweet,
    color: "spiders-accent-400",
    verb: "reposted your post",
  },
  follow: {
    icon: FaUserPlus,
    color: "spiders-primary-400",
    verb: "started following you",
  },
  mention: {
    icon: FaAt,
    color: "spiders-warning",
    verb: "mentioned you",
  },
};

const ToastNotification = ({ notification, onDismiss, allUsers = [] }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { condition } = useContext(SurveyContext);
  const [showQuickReply, setShowQuickReply] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [replySent, setReplySent] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const startRef = useRef(Date.now());
  const remainingRef = useRef(TOAST_DURATION);

  // Timer logic
  useEffect(() => {
    if (replySent) return;

    const tick = () => {
      if (!isPaused) {
        const now = Date.now();
        const delta = now - startRef.current;
        const newElapsed = TOAST_DURATION - remainingRef.current + delta;
        setElapsed(newElapsed);

        if (newElapsed >= TOAST_DURATION) {
          onDismiss();
          return;
        }
      }
      timerRef.current = requestAnimationFrame(tick);
    };

    timerRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(timerRef.current);
  }, [isPaused, replySent, onDismiss]);

  const handlePause = () => {
    setIsPaused(true);
    remainingRef.current = TOAST_DURATION - elapsed;
  };

  const handleResume = () => {
    startRef.current = Date.now();
    setIsPaused(false);
  };

  const config = typeConfig[notification.type] || typeConfig.comment;
  const Icon = config.icon;
  const progress = Math.max(0, 1 - elapsed / TOAST_DURATION);

  const canQuickReply =
    notification.type === "comment" ||
    notification.type === "reply" ||
    notification.type === "mention" ||
    notification.type === "thread_activity";

  const mentionableUsers = useMemo(() => {
    const users = [];
    const seen = new Set();
    if (notification.actorUsername) {
      users.push({
        username: notification.actorUsername,
        _id: notification.actorId,
      });
      seen.add(notification.actorUsername);
    }
    allUsers.forEach((u) => {
      if (u.username && !seen.has(u.username) && u.username !== user?.username) {
        users.push(u);
        seen.add(u.username);
      }
    });
    return users.filter((u) => u.username !== user?.username);
  }, [notification, allUsers, user?.username]);

  const handleQuickReply = async () => {
    if (!replyContent.trim()) return;
    try {
      await api.post("/comments", {
        messageId: notification.targetId,
        content: replyContent.trim(),
        condition,
      });
      setReplySent(true);
      setReplyContent("");
      setTimeout(() => onDismiss(), 1500);
    } catch (error) {
      console.error("Error sending quick reply:", error);
    }
  };

  const handleBodyClick = () => {
    if (notification.targetType === "message" && notification.targetId) {
      const commentParam = notification.sourceCommentId ? `&commentId=${notification.sourceCommentId}` : '';
      navigate(`/messages/${notification.targetId}?condition=${condition}${commentParam}`);
      onDismiss();
    } else if (notification.targetType === "user") {
      navigate(
        `/profile/${notification.actorUsername || notification.actorId}?condition=${condition}`
      );
      onDismiss();
    }
  };

  if (replySent) {
    return (
      <div className="toast-slide-in bg-spiders-bg-elevated border border-spiders-success/40 rounded-xl p-4 shadow-2xl w-80 sm:w-96">
        <div className="flex items-center space-x-2 text-spiders-success">
          <FaRegComment className="w-4 h-4" />
          <span className="text-sm font-medium">Reply sent!</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="toast-slide-in bg-spiders-bg-elevated border border-spiders-border rounded-xl shadow-2xl w-80 sm:w-96 overflow-hidden"
      onMouseEnter={handlePause}
      onMouseLeave={handleResume}
    >
      {/* Main content */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div
            className="flex items-start space-x-3 flex-1 min-w-0 cursor-pointer"
            onClick={handleBodyClick}
          >
            <img
              src={getProfilePicture(notification.actorUsername || "unknown", notification.actorAvatarSeed)}
              alt={notification.actorUsername}
              className="w-10 h-10 rounded-full flex-shrink-0 border border-spiders-border"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-1.5 mb-0.5">
                <Icon className={`w-3.5 h-3.5 text-${config.color}`} />
                <span className="text-sm text-spiders-text-primary">
                  <span className="font-bold">
                    {notification.actorUsername || "Someone"}
                  </span>{" "}
                  {config.verb}
                </span>
              </div>
              {notification.contentPreview && (
                <p className={`text-xs text-spiders-text-secondary italic mt-0.5 ${showQuickReply ? '' : 'line-clamp-2'}`}>
                  "{notification.contentPreview}"
                </p>
              )}
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            className="text-spiders-text-muted hover:text-spiders-text-primary p-1 rounded-full hover:bg-spiders-bg-surface transition-colors flex-shrink-0 ml-2"
          >
            <FaTimes className="w-3 h-3" />
          </button>
        </div>

        {/* Quick reply toggle + view thread */}
        {canQuickReply && !showQuickReply && (
          <div className="flex items-center space-x-3 mt-2">
            <button
              className="text-xs text-spiders-primary-400 hover:text-spiders-primary-300 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setShowQuickReply(true);
                setIsPaused(true);
              }}
            >
              Quick reply...
            </button>
            <button
              className="text-xs text-spiders-text-muted hover:text-spiders-text-secondary transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                handleBodyClick();
              }}
            >
              View thread
            </button>
          </div>
        )}

        {/* Quick reply input */}
        {showQuickReply && (
          <div className="mt-3" onClick={(e) => e.stopPropagation()}>
            <MentionInput
              value={replyContent}
              onChange={setReplyContent}
              placeholder="Write a quick reply..."
              rows={2}
              className="text-xs"
              mentionableUsers={mentionableUsers}
            />
            <div className="flex justify-end items-center space-x-2 mt-2">
              <button
                className="text-spiders-text-muted hover:text-spiders-text-secondary text-xs px-2 py-1"
                onClick={() => {
                  setShowQuickReply(false);
                  setIsPaused(false);
                  startRef.current = Date.now();
                }}
              >
                Cancel
              </button>
              <button
                className="spiders-btn-primary text-xs py-1 px-3"
                onClick={handleQuickReply}
                disabled={!replyContent.trim()}
              >
                Reply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Timer bar */}
      <div className="h-0.5 bg-spiders-bg-surface">
        <div
          className={`h-full bg-${config.color} transition-none`}
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </div>
  );
};

export default ToastNotification;
